import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ApiService from '../services/apiService';
import i18n from '../i18n/config';
import { TemplateSuggestion, TemplateSuggestionStatus, TemplateComponent } from '../types';
import { logger } from '../utils/logger';
import { getTemplateRef, templateEditorPath } from '../utils/templateRef';

const ALL_COMPONENTS: TemplateComponent[] = ['content', 'task', 'system_message', 'title', 'description'];

const STATUS_STYLES: Record<TemplateSuggestionStatus, { color: string; bg: string; border: string }> = {
    open: { color: 'text-primary-700', bg: 'bg-primary-50', border: 'border-primary-200' },
    accepted: { color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
    applied: { color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' },
    rejected: { color: 'text-gray-500', bg: 'bg-gray-50', border: 'border-gray-200' },
    failed: { color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' },
};

const STATUS_TAB_VALUES: (TemplateSuggestionStatus | 'all')[] = ['all', 'open', 'applied', 'rejected', 'failed'];

const getStatusLabel = (status: TemplateSuggestionStatus) => i18n.t(`templateSuggestions.status.${status}`);

const getComponentLabel = (comp: TemplateComponent) => i18n.t(`templateSuggestions.components.${comp}`);

const getTemplateDisplayName = (suggestion: Pick<TemplateSuggestion, 'template_id' | 'template_name' | 'template_title' | 'template'>) =>
    suggestion.template?.title || suggestion.template_title || suggestion.template_name || i18n.t('templateSuggestions.fallbackTemplateName', { id: suggestion.template_id });

const TemplateSuggestionsPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const api = useMemo(() => new ApiService(), []);

    // Data state
    const [suggestions, setSuggestions] = useState<TemplateSuggestion[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Filter state
    const [statusFilter, setStatusFilter] = useState<TemplateSuggestionStatus | 'all'>('all');
    const [templateFilter, setTemplateFilter] = useState<string>('all');

    // Edit state
    const [isEditing, setIsEditing] = useState(false);
    const [editInstruction, setEditInstruction] = useState('');
    const [editComponents, setEditComponents] = useState<TemplateComponent[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    // Action state
    const [isAccepting, setIsAccepting] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [showRejectDialog, setShowRejectDialog] = useState(false);

    const selected = suggestions.find(s => s.id === selectedId) || null;

    const loadSuggestions = useCallback(async () => {
        try {
            setError('');
            setLoading(true);
            const data = await api.getTemplateSuggestions({ backfill: true });
            setSuggestions(data);
        } catch (e: any) {
            setError(t('templateSuggestions.messages.loadError'));
            logger.error(e);
        } finally {
            setLoading(false);
        }
    }, [api, t]);

    useEffect(() => {
        loadSuggestions();
    }, [loadSuggestions]);

    // Derive unique templates for filter dropdown
    const uniqueTemplates = Array.from(
        new Map(
            suggestions
                .filter(s => s.template_title || s.template_name)
                .map(s => [s.template_id, { id: s.template_id, title: getTemplateDisplayName(s) }])
        ).values()
    );

    // Filtered list
    const filtered = suggestions.filter(s => {
        if (statusFilter !== 'all' && s.status !== statusFilter) return false;
        if (templateFilter !== 'all' && s.template_id.toString() !== templateFilter) return false;
        return true;
    });

    // Select a suggestion and load detail
    const handleSelect = (s: TemplateSuggestion) => {
        setSelectedId(s.id);
        setIsEditing(false);
        setShowRejectDialog(false);
        setSuccess('');
        setError('');
    };

    // Enter edit mode
    const handleStartEdit = () => {
        if (!selected) return;
        setEditInstruction(selected.normalized_instruction);
        setEditComponents([...selected.target_components]);
        setIsEditing(true);
    };

    // Save edits via PATCH
    const handleSaveEdit = async () => {
        if (!selected) return;
        try {
            setIsSaving(true);
            setError('');
            const updated = await api.updateTemplateSuggestion(selected.id, {
                normalized_instruction: editInstruction,
                target_components: editComponents,
            });
            setSuggestions(prev => prev.map(s => s.id === updated.id ? updated : s));
            setIsEditing(false);
            setSuccess(t('templateSuggestions.messages.updated'));
        } catch (e: any) {
            setError(e?.message || t('templateSuggestions.messages.saveError'));
        } finally {
            setIsSaving(false);
        }
    };

    const toggleComponent = (comp: TemplateComponent) => {
        setEditComponents(prev =>
            prev.includes(comp) ? prev.filter(c => c !== comp) : [...prev, comp]
        );
    };

    // Accept
    const handleAccept = async () => {
        if (!selected) return;
        try {
            setIsAccepting(true);
            setError('');
            await api.acceptTemplateSuggestion(selected.id, {
                model: 'gemini-flash',
                normalized_instruction: isEditing ? editInstruction : selected.normalized_instruction,
                target_components: isEditing ? editComponents : (selected.target_components.length > 0 ? selected.target_components : undefined),
            });
            setIsEditing(false);
            setSuccess(t('templateSuggestions.messages.applying'));
            await loadSuggestions();
        } catch (e: any) {
            setError(e?.message || t('templateSuggestions.messages.acceptError'));
        } finally {
            setIsAccepting(false);
        }
    };

    // Reject
    const handleReject = async () => {
        if (!selected) return;
        try {
            setIsRejecting(true);
            setError('');
            await api.rejectTemplateSuggestion(selected.id, {
                reason: rejectReason || undefined,
            });
            setShowRejectDialog(false);
            setRejectReason('');
            setSuccess(t('templateSuggestions.messages.rejected'));
            await loadSuggestions();
        } catch (e: any) {
            setError(e?.message || t('templateSuggestions.messages.rejectError'));
        } finally {
            setIsRejecting(false);
        }
    };

    // Retry (same as accept)
    const handleRetry = () => handleAccept();

    const formatDate = (dateStr: string) => {
        try {
            return new Intl.DateTimeFormat('nl-NL', {
                day: 'numeric', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
            }).format(new Date(dateStr));
        } catch { return dateStr; }
    };

    return (
        <div className="min-h-screen bg-background pb-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Page header */}
                <div className="mb-8 animate-fade-in flex items-center justify-between">
                    <div>
                        <h1 className="font-serif text-3xl" style={{ color: 'var(--text-strong)' }}>{t('templateSuggestions.header.title')}</h1>
                        <p className="text-gray-600 mt-2">{t('templateSuggestions.header.subtitle')}</p>
                    </div>
                    <button
                        onClick={loadSuggestions}
                        disabled={loading}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
                    >
                        <svg className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        {loading ? t('templateSuggestions.header.refreshing') : t('templateSuggestions.header.refresh')}
                    </button>
                </div>

                {/* Messages */}
                {error && (
                    <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 animate-fade-in">
                        <div className="flex">
                            <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            <p className="ml-3 text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                )}
                {success && (
                    <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4 animate-fade-in">
                        <div className="flex">
                            <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <p className="ml-3 text-sm text-green-700">{success}</p>
                        </div>
                    </div>
                )}

                {/* Filters */}
                <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center animate-fade-in">
                    {/* Status tabs */}
                    <div className="flex gap-1 bg-white rounded-lg p-1 shadow-soft border border-gray-100">
                        {STATUS_TAB_VALUES.map(value => (
                            <button
                                key={value}
                                onClick={() => setStatusFilter(value)}
                                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                                    statusFilter === value
                                        ? 'bg-primary-600 text-white shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                }`}
                            >
                                {t(`templateSuggestions.tabs.${value}`)}
                                {value !== 'all' && (
                                    <span className={`ml-1.5 text-xs ${statusFilter === value ? 'text-white/70' : 'text-gray-400'}`}>
                                        {suggestions.filter(s => s.status === value).length}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Template filter */}
                    {uniqueTemplates.length > 1 && (
                        <select
                            value={templateFilter}
                            onChange={e => setTemplateFilter(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        >
                            <option value="all">{t('templateSuggestions.filter.allTemplates')}</option>
                            {uniqueTemplates.map(t => (
                                <option key={t.id} value={t.id.toString()}>{t.title}</option>
                            ))}
                        </select>
                    )}
                </div>

                {/* Loading state */}
                {loading && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-1 space-y-3">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="bg-white rounded-xl p-4 shadow-soft animate-pulse">
                                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
                                    <div className="h-3 bg-gray-100 rounded w-full mb-2"></div>
                                    <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                                </div>
                            ))}
                        </div>
                        <div className="lg:col-span-2">
                            <div className="bg-white rounded-2xl p-8 shadow-soft animate-pulse">
                                <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
                                <div className="h-4 bg-gray-100 rounded w-full mb-3"></div>
                                <div className="h-4 bg-gray-100 rounded w-3/4 mb-3"></div>
                                <div className="h-32 bg-gray-100 rounded w-full"></div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Empty state */}
                {!loading && filtered.length === 0 && (
                    <div className="bg-white rounded-2xl shadow-soft p-12 text-center animate-fade-in">
                        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {statusFilter === 'all'
                                ? t('templateSuggestions.empty.titleAll')
                                : t('templateSuggestions.empty.titleFiltered', { status: getStatusLabel(statusFilter as TemplateSuggestionStatus).toLowerCase() })}
                        </h3>
                        <p className="text-gray-500 max-w-sm mx-auto">
                            {t('templateSuggestions.empty.body', { status: statusFilter !== 'all' ? getStatusLabel(statusFilter as TemplateSuggestionStatus).toLowerCase() + ' ' : '' })}
                        </p>
                    </div>
                )}

                {/* Main content: list + detail */}
                {!loading && filtered.length > 0 && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-in">
                        {/* Suggestion list */}
                        <div className="lg:col-span-1 space-y-2 max-h-[calc(100vh-280px)] overflow-y-auto pr-1">
                            {filtered.map(s => {
                                const cfg = STATUS_STYLES[s.status];
                                const isSelected = s.id === selectedId;
                                return (
                                    <button
                                        key={s.id}
                                        onClick={() => handleSelect(s)}
                                        className={`w-full text-left p-4 rounded-xl transition-all border ${
                                            isSelected
                                                ? 'bg-white shadow-medium border-primary-200 ring-1 ring-primary-100'
                                                : 'bg-white shadow-soft border-transparent hover:shadow-medium hover:border-gray-100'
                                        }`}
                                    >
                                        <div className="flex items-start justify-between gap-2 mb-2">
                                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${cfg.bg} ${cfg.color} border ${cfg.border}`}>
                                                {getStatusLabel(s.status)}
                                            </span>
                                        </div>
                                        <div className="mb-2">
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400">
                                                {t('templateSuggestions.list.templateLabel')}
                                            </p>
                                            <h4 className="text-sm font-semibold text-gray-900 line-clamp-1">
                                                {getTemplateDisplayName(s)}
                                            </h4>
                                        </div>
                                        <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                                            {s.normalized_instruction}
                                        </p>
                                        <div className="flex items-center justify-between text-xs text-gray-400">
                                            <span>{formatDate(s.created_at)}</span>
                                            {s.confidence !== undefined && s.confidence !== null && (
                                                <span className="flex items-center gap-1">
                                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                    </svg>
                                                    {Math.round(s.confidence * 100)}%
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Detail panel */}
                        <div className="lg:col-span-2">
                            {!selected ? (
                                <div className="bg-white rounded-2xl shadow-soft p-12 text-center">
                                    <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                                        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                                        </svg>
                                    </div>
                                    <p className="text-gray-500 text-sm">{t('templateSuggestions.detail.selectPrompt')}</p>
                                </div>
                            ) : (
                                <div className="bg-white rounded-2xl shadow-soft p-6 sm:p-8 animate-fade-in">
                                    {/* Detail header */}
                                    <div className="flex items-start justify-between mb-6">
                                        <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-400 mb-1">
                                                {t('templateSuggestions.detail.templateLabel')}
                                            </p>
                                            <div className="flex items-center gap-3 mb-1">
                                                <h2 className="text-xl font-semibold text-gray-900">
                                                    {getTemplateDisplayName(selected)}
                                                </h2>
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[selected.status].bg} ${STATUS_STYLES[selected.status].color} border ${STATUS_STYLES[selected.status].border}`}>
                                                    {getStatusLabel(selected.status)}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-500">
                                                {t('templateSuggestions.detail.likelyChange', { date: formatDate(selected.created_at) })}
                                            </p>
                                        </div>
                                        {selected.template && (
                                            <button
                                                type="button"
                                                onClick={() => navigate(templateEditorPath(getTemplateRef(selected.template!)))}
                                                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors whitespace-nowrap"
                                            >
                                                {t('templateSuggestions.detail.openTemplate')}
                                            </button>
                                        )}
                                    </div>

                                    {/* Original feedback */}
                                    <div className="mb-6">
                                        <h3 className="text-sm font-medium text-gray-700 mb-2">{t('templateSuggestions.detail.originalFeedback')}</h3>
                                        <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700 border border-gray-100">
                                            {selected.original_feedback}
                                        </div>
                                    </div>

                                    {/* Normalized instruction */}
                                    <div className="mb-6">
                                        <h3 className="text-sm font-medium text-gray-700 mb-2">{t('templateSuggestions.detail.proposedInstruction')}</h3>
                                        {isEditing ? (
                                            <textarea
                                                value={editInstruction}
                                                onChange={e => setEditInstruction(e.target.value)}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-sm"
                                                rows={4}
                                                placeholder={t('templateSuggestions.detail.instructionPlaceholder')}
                                            />
                                        ) : (
                                            <div className="bg-primary-50 rounded-xl p-4 text-sm text-gray-800 border border-primary-100">
                                                {selected.normalized_instruction}
                                            </div>
                                        )}
                                    </div>

                                    {/* Target components */}
                                    <div className="mb-6">
                                        <h3 className="text-sm font-medium text-gray-700 mb-2">{t('templateSuggestions.detail.targetComponents')}</h3>
                                        {isEditing ? (
                                            <div className="flex flex-wrap gap-2">
                                                {ALL_COMPONENTS.map(comp => (
                                                    <label
                                                        key={comp}
                                                        className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all text-sm ${
                                                            editComponents.includes(comp)
                                                                ? 'bg-primary-50 border-primary-300 text-primary-800'
                                                                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                                                        }`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={editComponents.includes(comp)}
                                                            onChange={() => toggleComponent(comp)}
                                                            className="sr-only"
                                                        />
                                                        <span className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                                                            editComponents.includes(comp)
                                                                ? 'bg-primary-600 border-primary-600'
                                                                : 'border-gray-300'
                                                        }`}>
                                                            {editComponents.includes(comp) && (
                                                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                                </svg>
                                                            )}
                                                        </span>
                                                        {getComponentLabel(comp)}
                                                    </label>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="flex flex-wrap gap-2">
                                                {selected.target_components.length > 0 ? (
                                                    selected.target_components.map(comp => (
                                                        <span key={comp} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                                                            {getComponentLabel(comp) || comp}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="text-sm text-gray-400 italic">{t('templateSuggestions.detail.autoDetermined')}</span>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Confidence & reasoning */}
                                    {(selected.confidence !== undefined && selected.confidence !== null) && (
                                        <div className="mb-6">
                                            <h3 className="text-sm font-medium text-gray-700 mb-2">{t('templateSuggestions.detail.confidence')}</h3>
                                            <div className="flex items-center gap-3 mb-2">
                                                <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                                                    <div
                                                        className={`h-2 rounded-full transition-all duration-500 ${
                                                            selected.confidence >= 0.7 ? 'bg-green-500' :
                                                            selected.confidence >= 0.4 ? 'bg-amber-500' : 'bg-red-500'
                                                        }`}
                                                        style={{ width: `${Math.round(selected.confidence * 100)}%` }}
                                                    />
                                                </div>
                                                <span className="text-sm font-medium text-gray-700 tabular-nums w-10 text-right">
                                                    {Math.round(selected.confidence * 100)}%
                                                </span>
                                            </div>
                                            {selected.reasoning && (
                                                <p className="text-sm text-gray-500 mt-2 leading-relaxed">{selected.reasoning}</p>
                                            )}
                                        </div>
                                    )}

                                    {/* Resolution note */}
                                    {selected.resolution_note && (
                                        <div className="mb-6">
                                            <h3 className="text-sm font-medium text-gray-700 mb-2">{t('templateSuggestions.detail.note')}</h3>
                                            <p className="text-sm text-gray-600 bg-gray-50 rounded-xl p-3 border border-gray-100">{selected.resolution_note}</p>
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div className="border-t border-gray-100 pt-6">
                                        {/* Open status: full actions */}
                                        {selected.status === 'open' && !showRejectDialog && (
                                            <div className="flex flex-wrap gap-3">
                                                {isEditing ? (
                                                    <>
                                                        <button
                                                            onClick={handleSaveEdit}
                                                            disabled={isSaving}
                                                            className="inline-flex items-center px-4 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 transition-colors shadow-sm"
                                                        >
                                                            {isSaving ? (
                                                                <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                                </svg>
                                                            ) : null}
                                                            {t('common.save')}
                                                        </button>
                                                        <button
                                                            onClick={() => setIsEditing(false)}
                                                            className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                                                        >
                                                            {t('common.cancel')}
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            onClick={handleAccept}
                                                            disabled={isAccepting}
                                                            className="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 transition-colors shadow-md hover:shadow-lg hover:-translate-y-0.5"
                                                        >
                                                            {isAccepting ? (
                                                                <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                                </svg>
                                                            ) : (
                                                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                </svg>
                                                            )}
                                                            {t('templateSuggestions.actions.apply')}
                                                        </button>
                                                        <button
                                                            onClick={handleStartEdit}
                                                            className="inline-flex items-center px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                                                        >
                                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                            </svg>
                                                            {t('templateSuggestions.actions.editProposal')}
                                                        </button>
                                                        <button
                                                            onClick={() => setShowRejectDialog(true)}
                                                            className="inline-flex items-center px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-500 bg-white hover:bg-gray-50 hover:text-red-600 hover:border-red-200 transition-colors"
                                                        >
                                                            {t('templateSuggestions.actions.doNotApply')}
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {/* Reject dialog */}
                                        {selected.status === 'open' && showRejectDialog && (
                                            <div className="bg-red-50 rounded-xl p-4 border border-red-100 animate-fade-in">
                                                <h4 className="text-sm font-medium text-red-800 mb-2">{t('templateSuggestions.actions.rejectTitle')}</h4>
                                                <textarea
                                                    value={rejectReason}
                                                    onChange={e => setRejectReason(e.target.value)}
                                                    placeholder={t('templateSuggestions.actions.rejectPlaceholder')}
                                                    className="w-full px-3 py-2 border border-red-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent mb-3"
                                                    rows={2}
                                                />
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={handleReject}
                                                        disabled={isRejecting}
                                                        className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
                                                    >
                                                        {isRejecting && (
                                                            <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                            </svg>
                                                        )}
                                                        {t('templateSuggestions.actions.confirmReject')}
                                                    </button>
                                                    <button
                                                        onClick={() => { setShowRejectDialog(false); setRejectReason(''); }}
                                                        className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                                                    >
                                                        {t('common.cancel')}
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Accepted status */}
                                        {selected.status === 'accepted' && (
                                            <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
                                                <svg className="animate-spin w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                </svg>
                                                <span className="text-sm font-medium text-amber-800">{t('templateSuggestions.statusBlocks.applyingToTemplate')}</span>
                                            </div>
                                        )}

                                        {/* Applied status */}
                                        {selected.status === 'applied' && (
                                            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl border border-green-200">
                                                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <span className="text-sm font-medium text-green-800">{t('templateSuggestions.statusBlocks.appliedSuccess')}</span>
                                            </div>
                                        )}

                                        {/* Rejected status */}
                                        {selected.status === 'rejected' && (
                                            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                                                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                                </svg>
                                                <span className="text-sm text-gray-600">{t('templateSuggestions.statusBlocks.rejected')}</span>
                                            </div>
                                        )}

                                        {/* Failed status */}
                                        {selected.status === 'failed' && (
                                            <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                                                <div className="flex items-center gap-3 mb-3">
                                                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <span className="text-sm font-medium text-red-800">{t('templateSuggestions.statusBlocks.applyFailed')}</span>
                                                </div>
                                                <button
                                                    onClick={handleRetry}
                                                    disabled={isAccepting}
                                                    className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
                                                >
                                                    {isAccepting ? (
                                                        <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                        </svg>
                                                    ) : (
                                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                        </svg>
                                                    )}
                                                    {t('common.retry')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TemplateSuggestionsPage;
